import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { BypassLayout, Modal } from '../components/common';
import PresetModal from '../components/PresetPage/PresetModal';
import FolderView from '../components/PresetPage/FolderView';
import ExamsByPresetModal from '../components/PresetPage/ExamsByPresetModal';
import { Bookmark, User, Users } from 'lucide-react';
import FloatingButton from '../components/common/FloatingButton';

import { PrintPreviewModal } from '../components/printPreview';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../editor/nodes/PlaygroundNodes';
import { processMathEquation } from '../components/utils/latexParser';

function PresetPage() {
  const { getAccessToken } = useAuth();
  const { isExpanded } = useSidebar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightPresetId = searchParams.get('highlight') ? parseInt(searchParams.get('highlight')) : null;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [treeList, setTreeList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStamp, setSelectedStamp] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTreeId, setSelectedTreeId] = useState(null);
  const [myBookmarks, setMyBookmarks] = useState([]);
  const [myPresets, setMyPresets] = useState([]);
  const [publishedPresets, setPublishedPresets] = useState([]);
  const [activeTab, setActiveTab] = useState('myPresets');
  
  // 검색 필터 상태 추가
  const [filters, setFilters] = useState({
    myBookmarks: {
      preset_name: '',
      author_name: '',
      description: ''
    },
    myPresets: {
      preset_name: '',
      author_name: '',
      description: ''
    },
    publishedPresets: {
      preset_name: '',
      author_name: '',
      description: ''
    }
  });
  
  // 페이지네이션 상태 추가
  const [myPresetsPage, setMyPresetsPage] = useState(1);
  const [myPresetsTotalPages, setMyPresetsTotalPages] = useState(1);
  const [publishedPresetsPage, setPublishedPresetsPage] = useState(1);
  const [publishedPresetsTotalPages, setPublishedPresetsTotalPages] = useState(1);
  const perPage = 10; // 페이지당 항목 수

  // 인피니티 스크롤 상태 (모바일 & 데스크톱)
  const [myPresetsLoadedPages, setMyPresetsLoadedPages] = useState(1);
  const [publishedPresetsLoadedPages, setPublishedPresetsLoadedPages] = useState(1);
  const [myBookmarksLoadedPages, setMyBookmarksLoadedPages] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMyPresets, setHasMoreMyPresets] = useState(true);
  const [hasMorePublishedPresets, setHasMorePublishedPresets] = useState(true);
  // hasMoreMyBookmarks는 제거 - 북마크는 전체 데이터를 한 번에 받음
  const contentScrollRef = useRef(null);
  const isLoadingRef = useRef(false); // 중복 호출 방지용 ref
  const lastScrollCallRef = useRef(0); // 스크롤 호출 시간 추적
  const lastFetchCallRef = useRef({}); // 각 API 호출 시간 추적 (중복 방지)
  const backgroundLoadTimeoutRef = useRef(null); // 백그라운드 로딩 타이머 추적
  
  // 검색 모드 상태
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchPages, setSearchPages] = useState({
    myBookmarks: 1,
    myPresets: 1,
    publishedPresets: 1
  });
  
  // 인피니티 스크롤 설정
  const desktopLoadCount = 12; // 데스크톱: 매 호출마다 12개
  const mobileLoadCount = 6; // 모바일: 매 호출마다 6개
  
  // 총 개수 상태 추가
  const [myBookmarksTotalCount, setMyBookmarksTotalCount] = useState(0);
  const [myPresetsTotalCount, setMyPresetsTotalCount] = useState(0);
  const [publishedPresetsTotalCount, setPublishedPresetsTotalCount] = useState(0);

  const [presetToEdit, setPresetToEdit] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // 초기 로드 상태 추가
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const initialLoadCompletedRef = useRef(false); // 초기 로드 완료 여부 추적


  // 인쇄 모달 상태 관리
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printTestData, setPrintTestData] = useState(null);

  // 시험 정보 모달 상태 관리
  const [isExamModalOpen, setIsExamModalOpen] = useState(false);
  const [examsByPreset, setExamsByPreset] = useState([]);
  const [selectedPresetName, setSelectedPresetName] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [isExamLoading, setIsExamLoading] = useState(false);

  // Lexical 데이터를 HTML로 변환하는 함수
  const convertLexicalToHtml = useCallback((lexicalData) => {
    const editor = createEditor({
      nodes: [...PlaygroundNodes]
    });

    const processNode = (node) => {
      if (node.type === 'equation') {
        node.equation = processMathEquation(node.equation);
      } else if (node.type === 'box-content') {
        node.className = 'PlaygroundEditorTheme__boxContent';
      }
      if (node.children && Array.isArray(node.children)) {
        node.children = node.children.map(child => processNode(child));
      }
      return node;
    };

    lexicalData.root = processNode(lexicalData.root);
    const editorState = editor.parseEditorState(lexicalData);

    let content = '';
    editor.setEditorState(editorState);
    editor.update(() => {
      content = $generateHtmlFromNodes(editor);
    });

    return content;
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = await getAccessToken();
        if (!token) {
          navigate('/signin');
        }
      } catch (error) {
        console.error('인증 확인 오류:', error);
        navigate('/signin');
      }
    };
    checkAuth();
  }, [getAccessToken, navigate]);

  // 교육과정 목록 가져오기
  const fetchTreeList = async () => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/testset/lists`, {
        headers: { 'Authorization': token }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        setTreeList(data.data);
        setError(null);
      }
      return data;
    } catch (error) {
      console.error('교육과정 목록 조회 실패:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // fetchInitialPresets는 더 이상 사용하지 않음
  // 각 탭의 데이터를 별도로 가져와서 정확한 total_items를 설정하기 위해
  // fetchMyPresets, fetchPublishedPresets, fetchBookmarks를 직접 호출

  // 프리셋 검색 함수
  const searchPresets = async (page = 1, append = false) => {
    // 중복 호출 방지: 같은 검색을 500ms 내에 다시 호출하면 무시
    const now = Date.now();
    const lastCallKey = `search_${activeTab}_${page}_${append}`;
    if (lastFetchCallRef.current[lastCallKey] && (now - lastFetchCallRef.current[lastCallKey]) < 500) {
      console.log('🚫 searchPresets 중복 호출 방지됨:', { activeTab, page, append });
      return;
    }
    lastFetchCallRef.current[lastCallKey] = now;
    
    console.log('✅ searchPresets 호출됨:', { activeTab, page, append });
    
    // 현재 탭에 따른 search_target 설정
    let searchTarget = '';
    if (activeTab === 'myBookmarks') {
      searchTarget = 'my_bookmarks';
    } else if (activeTab === 'myPresets') {
      searchTarget = 'my_presets';
    } else if (activeTab === 'publishedPresets') {
      searchTarget = 'public_presets';
    }
    
    // 현재 탭의 필터 가져오기
    const currentFilters = filters[activeTab];
    
    // 검색 조건이 없으면 일반 fetch 함수 호출
    const hasSearchCondition = Object.values(currentFilters).some(value => value.trim() !== '');
    if (!hasSearchCondition) {
      // 검색 조건이 없으면 각 탭의 기본 fetch 함수 호출
      if (activeTab === 'myBookmarks') {
        return fetchBookmarks();
      } else if (activeTab === 'myPresets') {
        return fetchMyPresets(page, append);
      } else if (activeTab === 'publishedPresets') {
        return fetchPublishedPresets(page, append);
      }
      return;
    }
    
    // 북마크 탭은 append 모드 지원하지 않음 (전체 데이터를 한 번에 받음)
    if (activeTab === 'myBookmarks' && append) {
      return;
    }
    
    // 중복 호출 방지
    if (append && isLoadingRef.current) return;
    
    if (!append) setIsLoading(true);
    if (append) {
      setIsLoadingMore(true);
      isLoadingRef.current = true;
    }
    
    try {
      const token = await getAccessToken();
      const isMobile = window.innerWidth < 768;
      const limit = isMobile ? mobileLoadCount : desktopLoadCount;
      
      // 쿼리 파라미터 구성 (북마크는 페이지네이션 파라미터 제외)
      let queryParams = activeTab === 'myBookmarks' 
        ? `search_target=${searchTarget}`
        : `page=${page}&per_page=${limit}&search_target=${searchTarget}`;
      
      // 필터 조건 추가 (하나만 선택 가능)
      if (currentFilters.preset_name) {
        queryParams += `&preset_name=${encodeURIComponent(currentFilters.preset_name)}`;
      } else if (currentFilters.author_name) {
        queryParams += `&author_name=${encodeURIComponent(currentFilters.author_name)}`;
      } else if (currentFilters.description) {
        queryParams += `&description=${encodeURIComponent(currentFilters.description)}`;
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/filtered?${queryParams}`,
        {
          headers: { 'Authorization': token }
        }
      );
      
      if (response.status === 404) {
        // 404는 데이터가 없는 경우로 처리
        if (!append) {
          if (activeTab === 'myBookmarks') {
            setMyBookmarks([]);
            setMyBookmarksTotalCount(0);
          } else if (activeTab === 'myPresets') {
            setMyPresets([]);
            setMyPresetsTotalCount(0);
            setHasMoreMyPresets(false);
          } else if (activeTab === 'publishedPresets') {
            setPublishedPresets([]);
            setPublishedPresetsTotalCount(0);
            setHasMorePublishedPresets(false);
          }
        }
        return;
      }
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        const newData = data.data || [];
        
        // 각 탭에 따라 데이터 설정
        if (activeTab === 'myBookmarks') {
          // 북마크는 전체 데이터를 한 번에 설정 (페이지네이션 없음)
          setMyBookmarks(newData);
          setMyBookmarksTotalCount(data.total_items || newData.length);
        } else if (activeTab === 'myPresets') {
          if (append) {
            setMyPresets(prev => [...prev, ...newData]);
          } else {
            setMyPresets(newData);
          }
          setMyPresetsTotalCount(data.total_items);
          setHasMoreMyPresets(newData.length >= limit && page < data.total_pages);
        } else if (activeTab === 'publishedPresets') {
          if (append) {
            setPublishedPresets(prev => [...prev, ...newData]);
          } else {
            setPublishedPresets(newData);
          }
          setPublishedPresetsTotalCount(data.total_items);
          setHasMorePublishedPresets(newData.length >= limit && page < data.total_pages);
        }
      }
    } catch (error) {
      console.error('프리셋 검색 실패:', error);
      setError(error.message);
    } finally {
      if (!append) setIsLoading(false);
      if (append) {
        setIsLoadingMore(false);
        isLoadingRef.current = false;
        
        // 로딩 완료 후 스크롤 위치 재체크
        setTimeout(() => {
          const element = contentScrollRef.current;
          if (element) {
            const distanceFromBottom = element.scrollHeight - (element.scrollTop + element.clientHeight);
            if (distanceFromBottom <= 50 && !isLoadingRef.current) {
              // 여전히 하단 근처에 있으면 다시 스크롤 이벤트 트리거
              element.dispatchEvent(new Event('scroll'));
            }
          }
        }, 100);
      }
    }
  };

  // 내 프리셋 목록 가져오기 (인피니티 스크롤)
  const fetchMyPresets = async (page = 1, append = false) => {
    // 중복 호출 방지: 같은 페이지를 500ms 내에 다시 호출하면 무시
    const now = Date.now();
    const lastCallKey = `myPresets_${page}_${append}`;
    if (lastFetchCallRef.current[lastCallKey] && (now - lastFetchCallRef.current[lastCallKey]) < 500) {
      console.log('🚫 fetchMyPresets 중복 호출 방지됨:', { page, append });
      return;
    }
    lastFetchCallRef.current[lastCallKey] = now;
    
    console.log('✅ fetchMyPresets 호출됨:', { page, append });
    
    // 기존 중복 호출 방지
    if (append && isLoadingRef.current) return;
    
    if (!append) setIsLoading(true);
    if (append) {
      setIsLoadingMore(true);
      isLoadingRef.current = true;
    }
    
    try {
      const token = await getAccessToken();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/mypresets?page=${page}&per_page=1000`, 
        {
          headers: { 'Authorization': token }
        }
      );
      
      if (response.status === 404) {
        // 404는 데이터가 없는 경우로 처리
        if (!append) {
          setMyPresets([]);
          setMyPresetsPage(1);
          setMyPresetsTotalPages(1);
        }
        setHasMoreMyPresets(false);
        return;
      }
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        const newData = data.data || [];
        if (append) {
          setMyPresets(prev => [...prev, ...newData]);
        } else {
          setMyPresets(newData);
        }
        setMyPresetsPage(data.page);
        setMyPresetsTotalPages(data.total_pages);

        // 총 개수 설정 (매번 업데이트)
        if (data.total_items !== undefined) {
          setMyPresetsTotalCount(data.total_items);
        }

        // 더 불러올 데이터가 있는지 확인
        setHasMoreMyPresets(newData.length >= 1000 && page < data.total_pages);
        return newData;
      }
      return [];
    } catch (error) {
      console.error('내 프리셋 목록 조회 실패:', error);
      setError(error.message);
      return [];
    } finally {
      if (!append) setIsLoading(false);
      if (append) {
        setIsLoadingMore(false);
        isLoadingRef.current = false;
        
        // 로딩 완료 후 스크롤 위치 재체크
        setTimeout(() => {
          const element = contentScrollRef.current;
          if (element) {
            const distanceFromBottom = element.scrollHeight - (element.scrollTop + element.clientHeight);
            if (distanceFromBottom <= 50 && !isLoadingRef.current) {
              // 여전히 하단 근처에 있으면 다시 스크롤 이벤트 트리거
              element.dispatchEvent(new Event('scroll'));
            }
          }
        }, 100);
      }
    }
  };

  // 공개된 프리셋 목록 가져오기 (인피니티 스크롤)
  const fetchPublishedPresets = async (page = 1, append = false) => {
    // 중복 호출 방지: 같은 페이지를 500ms 내에 다시 호출하면 무시
    const now = Date.now();
    const lastCallKey = `publishedPresets_${page}_${append}`;
    if (lastFetchCallRef.current[lastCallKey] && (now - lastFetchCallRef.current[lastCallKey]) < 500) {
      console.log('🚫 fetchPublishedPresets 중복 호출 방지됨:', { page, append });
      return;
    }
    lastFetchCallRef.current[lastCallKey] = now;
    
    console.log('✅ fetchPublishedPresets 호출됨:', { page, append });
    
    // 기존 중복 호출 방지
    if (append && isLoadingRef.current) return;
    
    if (!append) setIsLoading(true);
    if (append) {
      setIsLoadingMore(true);
      isLoadingRef.current = true;
    }
    
    try {
      const token = await getAccessToken();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/public?page=${page}&per_page=1000`, 
        {
          headers: { 'Authorization': token }
        }
      );
      
      if (response.status === 404) {
        // 404는 데이터가 없는 경우로 처리
        if (!append) {
          setPublishedPresets([]);
          setPublishedPresetsPage(1);
          setPublishedPresetsTotalPages(1);
        }
        setHasMorePublishedPresets(false);
        return;
      }
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        const newData = data.data || [];
        if (append) {
          setPublishedPresets(prev => [...prev, ...newData]);
        } else {
          setPublishedPresets(newData);
        }
        setPublishedPresetsPage(data.page);
        setPublishedPresetsTotalPages(data.total_pages);

        // 총 개수 설정 (매번 업데이트)
        if (data.total_items !== undefined) {
          setPublishedPresetsTotalCount(data.total_items);
        }

        // 더 불러올 데이터가 있는지 확인
        setHasMorePublishedPresets(newData.length >= 1000 && page < data.total_pages);
        return newData;
      }
      return [];
    } catch (error) {
      console.error('공개된 프리셋 목록 조회 실패:', error);
      setError(error.message);
      return [];
    } finally {
      if (!append) setIsLoading(false);
      if (append) {
        setIsLoadingMore(false);
        isLoadingRef.current = false;
        
        // 로딩 완료 후 스크롤 위치 재체크
        setTimeout(() => {
          const element = contentScrollRef.current;
          if (element) {
            const distanceFromBottom = element.scrollHeight - (element.scrollTop + element.clientHeight);
            if (distanceFromBottom <= 50 && !isLoadingRef.current) {
              // 여전히 하단 근처에 있으면 다시 스크롤 이벤트 트리거
              element.dispatchEvent(new Event('scroll'));
            }
          }
        }, 100);
      }
    }
  };

  // 즐겨찾기 목록 조회 함수 (전체 데이터를 한 번에 받음)
  const fetchBookmarks = async () => {
    // 중복 호출 방지: 500ms 내에 다시 호출하면 무시
    const now = Date.now();
    const lastCallKey = 'bookmarks';
    if (lastFetchCallRef.current[lastCallKey] && (now - lastFetchCallRef.current[lastCallKey]) < 500) {
      console.log('🚫 fetchBookmarks 중복 호출 방지됨');
      return;
    }
    lastFetchCallRef.current[lastCallKey] = now;
    
    console.log('✅ fetchBookmarks 호출됨');
    
    setIsLoading(true);
    
    try {
      const token = await getAccessToken();
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/bookmarks?per_page=1000`,
        {
          headers: { 'Authorization': token }
        }
      );
      
      if (response.status === 404) {
        // 404는 데이터가 없는 경우로 처리
        setMyBookmarks([]);
        setMyBookmarksTotalCount(0);
        return [];
      }
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        const bookmarkData = data.data || [];
        
        // 전체 데이터 설정
        setMyBookmarks(bookmarkData);
        
        // 총 개수는 받은 데이터의 길이로 설정
        setMyBookmarksTotalCount(bookmarkData.length);
        
        // 북마크는 전체를 한 번에 받으므로 인피니티 스크롤 불필요
        
        return bookmarkData;
      }
      return null;
    } catch (error) {
      console.error('즐겨찾기 목록 조회 실패:', error);
      setError(error.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // 단건 API로 프리셋 존재 확인
  const verifyPresetExists = async (presetId) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${presetId}`,
        { headers: { 'Authorization': token } }
      );
      if (response.status === 401) {
        navigate('/signin');
        return null;
      }
      if (!response.ok) return null;
      const data = await response.json();
      return data.status === 'success' ? data.data : null;
    } catch {
      return null;
    }
  };

  // 전체 페이지를 순회하며 프리셋 찾기
  const findPresetInAllPages = async (fetchFn, presetId) => {
    let page = 1;
    while (true) {
      const data = await fetchFn(page, page > 1) || [];
      if (data.some(p => p.preset_id === presetId)) return true;
      if (data.length < 1000) return false; // 마지막 페이지
      page++;
    }
  };

  // 즐겨찾기 목록 업데이트 함수
  const updateBookmarksList = async (presetIds) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/bookmarks`, {
        method: 'PUT',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ preset_ids: presetIds })
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        // 즐겨찾기 탭 데이터 갱신
        fetchBookmarks();
      }
    } catch (error) {
      console.error('즐겨찾기 목록 업데이트 실패:', error);
      setError(error.message);
    }
  };

  // 즐겨찾기 추가/제거 함수 수정
  const toggleBookmark = async (presetId, isCurrentlyBookmarked) => {
    try {
      if (isCurrentlyBookmarked) {
        // 즐겨찾기 제거: 현재 북마크 목록을 가져온 후, 해당 ID를 제외하고 업데이트
        const bookmarks = await fetchBookmarks();
        if (bookmarks) {
          const updatedBookmarkIds = bookmarks
            .filter(bookmark => bookmark.preset_id !== presetId)
            .map(bookmark => bookmark.preset_id);
          
          await updateBookmarksList(updatedBookmarkIds);
        }
      } else {
        // 즐겨찾기 추가: POST API 사용
        const token = await getAccessToken();
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/bookmarks`, {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ preset_id: presetId })
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            navigate('/signin');
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        if (data.status === 'success') {
          // 북마크 목록 갱신
          fetchBookmarks();
        }
      }
      
      // UI 상태 업데이트
      // 1. 내 프리셋 테이블에서 해당 프리셋 북마크 상태 변경
      setMyPresets(prevPresets => 
        prevPresets.map(preset => 
          preset.preset_id === presetId 
            ? { ...preset, bookmarked: !isCurrentlyBookmarked }
            : preset
        )
      );
      
      // 2. 공개된 프리셋 테이블에서 해당 프리셋 북마크 상태 변경
      setPublishedPresets(prevPresets => 
        prevPresets.map(preset => 
          preset.preset_id === presetId 
            ? { ...preset, bookmarked: !isCurrentlyBookmarked }
            : preset
        )
      );
      
    } catch (error) {
      console.error('즐겨찾기 변경 실패:', error);
      setError(error.message);
    }
  };

  // 프리셋 공개/비공개 상태 토글 함수
  const togglePublishStatus = async (presetId, isCurrentlyPublished) => {
    try {
      const token = await getAccessToken();
      const endpoint = isCurrentlyPublished 
        ? `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${presetId}/unpublish`
        : `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${presetId}/publish`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': token }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return;
        }
        
        if (response.status === 400) {
          // 이미 해당 상태인 경우 - 에러 메시지로 표시하지 않고 무시
          console.warn('이미 해당 상태입니다.');
          return;
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        // UI 상태 업데이트: 내 프리셋 목록에서 공개 상태 변경
        setMyPresets(prevPresets => 
          prevPresets.map(preset => 
            preset.preset_id === presetId 
              ? { ...preset, published: !isCurrentlyPublished }
              : preset
          )
        );
        
        // 데이터 다시 불러오기 - 공개 상태가 변경되면 공개된 프리셋 목록도 갱신 필요
        if (activeTab === 'publishedPresets') {
          fetchPublishedPresets(publishedPresetsPage);
        }
      }
    } catch (error) {
      console.error('프리셋 공개 상태 변경 실패:', error);
      setError(error.message);
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    // 이미 초기 로드가 완료되었다면 실행하지 않음
    if (initialLoadCompletedRef.current) {
      console.log('🚫 초기 데이터 로드 중복 방지됨:', { activeTab });
      return;
    }
    
    // 각 탭의 데이터를 별도로 가져와서 정확한 total_items를 설정
    // 현재 activeTab에 따라 해당 데이터만 우선 로드
    const loadInitialData = async () => {
      console.log('🚀 초기 데이터 로드 시작:', { activeTab, completed: initialLoadCompletedRef.current });

      // 즉시 완료 상태로 설정하여 중복 실행 방지
      initialLoadCompletedRef.current = true;

      // highlight 모드: 단건 확인 → 전체 페이지 순회 탐색
      if (highlightPresetId) {
        // 1. 단건 API로 존재 확인
        const presetData = await verifyPresetExists(highlightPresetId);
        if (!presetData) {
          alert('해당 커스텀 프리셋을 찾을 수 없습니다.');
          setSearchParams({}, { replace: true });
          // 기본 탭 데이터 로드
          await fetchMyPresets(1, false);
          backgroundLoadTimeoutRef.current = setTimeout(() => {
            fetchBookmarks();
            fetchPublishedPresets(1, false);
            backgroundLoadTimeoutRef.current = null;
          }, 500);
          setIsInitialLoad(false);
          return;
        }

        // 2. myPresets 전체 페이지 순회
        const foundInMy = await findPresetInAllPages(fetchMyPresets, highlightPresetId);
        if (foundInMy) {
          backgroundLoadTimeoutRef.current = setTimeout(() => {
            fetchBookmarks();
            fetchPublishedPresets(1, false);
            backgroundLoadTimeoutRef.current = null;
          }, 500);
          setIsInitialLoad(false);
          return;
        }

        // 3. publishedPresets 전체 페이지 순회
        setActiveTab('publishedPresets');
        const foundInPub = await findPresetInAllPages(fetchPublishedPresets, highlightPresetId);
        if (foundInPub) {
          backgroundLoadTimeoutRef.current = setTimeout(() => {
            fetchBookmarks();
            fetchMyPresets(1, false);
            backgroundLoadTimeoutRef.current = null;
          }, 500);
          setIsInitialLoad(false);
          return;
        }

        // 4. 어디서도 못 찾음
        alert('해당 커스텀 프리셋을 찾을 수 없습니다.');
        setSearchParams({}, { replace: true });
        setIsInitialLoad(false);
        return;
      }

      // 일반 모드
      if (activeTab === 'myPresets') {
        await fetchMyPresets(1, false);
      } else if (activeTab === 'publishedPresets') {
        await fetchPublishedPresets(1, false);
      } else if (activeTab === 'myBookmarks') {
        await fetchBookmarks();
      }

      // 다른 탭 데이터도 백그라운드에서 로드 (현재 탭 제외)
      if (backgroundLoadTimeoutRef.current) {
        clearTimeout(backgroundLoadTimeoutRef.current);
      }

      backgroundLoadTimeoutRef.current = setTimeout(() => {
        console.log('📦 백그라운드 데이터 로드:', { activeTab });
        if (activeTab !== 'publishedPresets') fetchPublishedPresets(1, false);
        if (activeTab !== 'myBookmarks') fetchBookmarks();
        if (activeTab !== 'myPresets') fetchMyPresets(1, false);
        backgroundLoadTimeoutRef.current = null;
      }, 500);

      // 초기 로드 완료 표시
      setIsInitialLoad(false);
    };
    
    loadInitialData();
    
    // 컴포넌트 언마운트 시 타이머 정리
    return () => {
      if (backgroundLoadTimeoutRef.current) {
        clearTimeout(backgroundLoadTimeoutRef.current);
        backgroundLoadTimeoutRef.current = null;
      }
    };
  }, [getAccessToken, navigate]);

  // 탭 변경 시 해당 탭의 데이터 로드
  useEffect(() => {
    console.log('🔄 탭 변경 useEffect 실행:', { activeTab, isInitialLoad });
    
    // 초기 로드 중에는 실행하지 않음 (중복 방지)
    if (isInitialLoad) return;
    
    // 탭 변경 시 인피니티 스크롤 상태 리셋
    setMyPresetsLoadedPages(1);
    setPublishedPresetsLoadedPages(1);
    setMyBookmarksLoadedPages(1);
    setIsLoadingMore(false);
    isLoadingRef.current = false; // ref도 리셋
    lastScrollCallRef.current = 0; // 스크롤 타임스탬프 리셋
    
    // 검색 모드 리셋 (탭 변경 시)
    setIsSearchMode(false);
    setSearchPages(prev => ({
      ...prev,
      [activeTab]: 1
    }));
    
    // 스크롤 위치 초기화
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
    
    // 현재 탭의 필터 확인
    const currentFilters = filters[activeTab];
    const hasSearchCondition = Object.values(currentFilters).some(value => value.trim() !== '');
    
    // 검색 조건이 있으면 검색, 없으면 일반 로드
    if (hasSearchCondition) {
      setIsSearchMode(true);
      searchPresets(1, false);
    } else {
      if (activeTab === 'myPresets') {
        fetchMyPresets(1, false);
      } else if (activeTab === 'publishedPresets') {
        fetchPublishedPresets(1, false);
      } else if (activeTab === 'myBookmarks') {
        fetchBookmarks(); // 북마크는 전체 데이터 한 번에 받음
      }
    }
  }, [activeTab, isInitialLoad]);

  // 인피니티 스크롤 이벤트 핸들러 (모바일 & 데스크톱)
  useEffect(() => {
    const handleScroll = () => {
      const element = contentScrollRef.current;
      if (!element) return;
      
      // 이미 로딩 중이면 중복 호출 방지 (ref 사용)
      if (isLoadingRef.current) return;
      
      // 스로틀링: 최소 100ms 간격으로만 호출 (300ms에서 단축)
      const now = Date.now();
      if (now - lastScrollCallRef.current < 100) return;
      
      const scrollThreshold = 50; // threshold를 좀 더 여유있게 조정
      const distanceFromBottom = element.scrollHeight - (element.scrollTop + element.clientHeight);
      const isNearBottom = distanceFromBottom <= scrollThreshold;
      
      // 정확히 하단에 도달했거나(distanceFromBottom <= 1) 근처에 있을 때
      if ((isNearBottom || distanceFromBottom <= 1) && !isLoadingRef.current) {
        lastScrollCallRef.current = now;
        
        // 하단에 도달했을 때
        // 북마크는 전체 데이터를 한 번에 받으므로 인피니티 스크롤 불필요
        if (activeTab === 'myPresets' && hasMoreMyPresets) {
          if (isSearchMode) {
            // 검색 모드일 때
            const nextPage = searchPages.myPresets + 1;
            setSearchPages(prev => ({ ...prev, myPresets: nextPage }));
            searchPresets(nextPage, true);
          } else {
            // 일반 모드일 때
            const nextPage = myPresetsLoadedPages + 1;
            setMyPresetsLoadedPages(nextPage);
            fetchMyPresets(nextPage, true);
          }
        } else if (activeTab === 'publishedPresets' && hasMorePublishedPresets) {
          if (isSearchMode) {
            // 검색 모드일 때
            const nextPage = searchPages.publishedPresets + 1;
            setSearchPages(prev => ({ ...prev, publishedPresets: nextPage }));
            searchPresets(nextPage, true);
          } else {
            // 일반 모드일 때
            const nextPage = publishedPresetsLoadedPages + 1;
            setPublishedPresetsLoadedPages(nextPage);
            fetchPublishedPresets(nextPage, true);
          }
        }
      }
    };

    // DOM이 마운트된 후 스크롤 이벤트 리스너 설정
    // setTimeout을 사용하여 다음 렌더 사이클에서 실행
    const timer = setTimeout(() => {
      const element = contentScrollRef.current;
      if (element) {
        element.addEventListener('scroll', handleScroll, { passive: true });
        
        // 초기 로드 시 스크롤 체크 한 번 실행 (모바일에서 필요할 수 있음)
        // 컨텐츠가 화면보다 짧을 경우 추가 데이터 로드
        if (element.scrollHeight <= element.clientHeight + 100) {
          handleScroll();
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      const element = contentScrollRef.current;
      if (element) {
        element.removeEventListener('scroll', handleScroll);
      }
    };
  }, [activeTab, myPresetsLoadedPages, publishedPresetsLoadedPages, 
      hasMoreMyPresets, hasMorePublishedPresets, isSearchMode, searchPages]);



  const handleCreateModalOpen = async () => {
    // 교육과정 목록이 아직 로드되지 않았다면 로드
    if (treeList.length === 0) {
      setIsLoading(true);
      setError(null); // 모달 열기 전에 에러 상태 초기화
      try {
        await fetchTreeList();
        setIsCreateModalOpen(true); // 에러가 없을 때만 모달 열기
      } catch (error) {
        alert("교육과정 정보를 불러오는 데 실패했습니다. 다시 시도해주세요.");
      } finally {
        setIsLoading(false);
      }
    } else {
      setError(null); // 기존 데이터를 사용할 때도 에러 상태 초기화
      setIsCreateModalOpen(true);
    }
  };

  const handleCreatePreset = async () => {
    if (!selectedTreeId) {
      alert("교육과정을 선택해주세요.");
      return;
    }

    try {
      // 선택한 트리 정보 찾기
      const selectedTree = treeList.find(tree => tree.tree_id === selectedTreeId);
      if (selectedTree) {
        // 트리 정보를 스탬프 형식으로 변환
        const stampData = {
          stamp_id: null,
          tree_id: selectedTree.tree_id,
          tree_name: selectedTree.tree_name,
          edu_ver: selectedTree.edu_ver,
          exam_count: 0
        };
        
        // 프리셋 모달 열기
        setSelectedStamp(stampData);
        setIsModalOpen(true);
        setIsCreateModalOpen(false);
        setSelectedTreeId(null);
      }
    } catch (error) {
      console.error('프리셋 생성 오류:', error);
    }
  };

  // 프리셋 삭제 함수
  const deletePreset = async (presetId) => {
    // 삭제 확인
    if (!window.confirm('정말로 이 프리셋을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return; // 사용자가 취소함
    }
    
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${presetId}`, {
        method: 'DELETE',
        headers: { 'Authorization': token }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        // UI 상태 업데이트: 내 프리셋 목록에서 삭제된 프리셋 제거
        setMyPresets(prevPresets => 
          prevPresets.filter(preset => preset.preset_id !== presetId)
        );
      }
    } catch (error) {
      console.error('프리셋 삭제 실패:', error);
      setError(error.message);
    }
  };

  // 프리셋 편집 함수
  const editPreset = async (presetId) => {
    try {
      setIsLoading(true);
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${presetId}`, {
        headers: { 'Authorization': token }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        const presetData = data.data;
        
        // 교육과정 정보를 스탬프 형식으로 변환
        const stampData = {
          tree_id: presetData.tree_id,
          tree_name: presetData.tree_name,
          edu_ver: presetData.edu_ver
        };
        
        setSelectedStamp(stampData);
        setPresetToEdit(presetData);
        setIsEditModalOpen(true);
      }
    } catch (error) {
      console.error('프리셋 편집 정보 로드 실패:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setPresetToEdit(null);
    setSelectedStamp(null);
  };



  // 프리셋 미리보기 가져오기 함수 (바로 PrintPreviewModal 열기)
  const previewPreset = async (presetId) => {
    try {
      setIsLoading(true);
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${presetId}`, {
        headers: { 'Authorization': token }
      });
      
      if (response.status === 404) {
        // 404 오류 발생 시 북마크 목록 새로고침
        await fetchBookmarks();
        // 현재 탭이 북마크 탭이면 내 프리셋 탭으로 이동
        if (activeTab === 'myBookmarks') {
          setActiveTab('myPresets');
        }
        alert('해당 시험지가 삭제되었거나 접근할 수 없습니다.');
        return;
      }
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        const presetData = data.data;
        
        // 바로 PrintPreviewModal용 testData 준비
        const testData = {
          problems_list: presetData.preset_info?.map(info => ({
            problem_id: info.problem_id
          })) || [],
          tree_name: presetData.tree_name || '',
          edu_ver: presetData.edu_ver || '',
          student_name: '', // 프리셋에서는 학생명이 없으므로 빈 문자열
          exam_id: presetData.preset_id || '',
          exam_type: 'custom', // 커스텀 프리셋 타입
          preset_id: presetData.preset_id, // 프리셋 ID 추가
          preset_name: presetData.preset_name || '' // 커스텀 이름 — v4 리뉴얼 헤더 부제용
        };

        setPrintTestData(testData);
        setIsPrintModalOpen(true);
      }
    } catch (error) {
      console.error('프리셋 미리보기 로드 실패:', error);
      setError(error.message);
      
      // 오류 발생 시 북마크 목록 새로고침
      await fetchBookmarks();
    } finally {
      setIsLoading(false);
    }
  };

  // 프리셋 복사 함수 추가
  const handleCopyPreset = async (presetId) => {
    try {
      setIsLoading(true);
      const token = await getAccessToken();
      
      // 프리셋 정보 가져오기
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${presetId}`, {
        headers: { 'Authorization': token }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        const presetData = data.data;
        
        // preset_info 데이터 준비
        const presetInfo = presetData.preset_info.map((info) => ({
          address: info.address,
          step: info.step,
          sequence: info.sequence,
          problem_id: info.problem_id
        }));
        
        // 새 프리셋 생성 요청 데이터
        const newPresetData = {
          preset_name: `(복사)${presetData.preset_name}`,
          description: presetData.description || '',
          preset_info: presetInfo
        };
        
        // 새 프리셋 생성 API 호출
        const createResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/mypresets`, {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newPresetData)
        });
        
        if (!createResponse.ok) {
          const errorData = await createResponse.json();
          throw new Error(errorData.message || `서버 오류가 발생했습니다. (상태 코드: ${createResponse.status})`);
        }
        
        // 복사 성공 처리
        alert("프리셋이 성공적으로 복사되었습니다.");
        
        // 내 프리셋 목록 갱신
        fetchMyPresets();
        
        // 탭을 내 프리셋으로 변경 (이미 내 커스텀 탭이 아닌 경우만)
        if (activeTab !== 'myPresets') {
          setActiveTab('myPresets');
        }
      }
    } catch (error) {
      console.error('프리셋 복사 실패:', error);
      setError(error.message);
      alert(`프리셋 복사 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 프리셋별 시험 정보 조회 함수
  const fetchExamsByPreset = async (presetId, presetName) => {
    setSelectedPresetName(presetName);
    setSelectedPresetId(presetId);

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/examsbypreset?preset_id=${presetId}`,
        {
          headers: { 'Authorization': token }
        }
      );

      if (response.status === 404) {
        setExamsByPreset([]);
        setIsExamModalOpen(true);
        return;
      }

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        const examList = data.data || [];

        // 학생 정보 조회하여 담당/강의 매칭
        try {
          const userType = localStorage.getItem('userType');
          const userId = localStorage.getItem('userId');
          const studentParams = new URLSearchParams({ page: 1, per_page: 1000 });
          const studentUrl = userType === 'manager'
            ? `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${studentParams}`
            : `${process.env.REACT_APP_API_BASE_URL}/api/users/student/allocate?${studentParams}&teacher_id=${userId}`;

          const studentRes = await fetch(studentUrl, {
            headers: { 'Authorization': token, 'Content-Type': 'application/json' }
          });

          if (studentRes.ok) {
            const studentData = await studentRes.json();
            if (studentData.status === 'success' && studentData.data) {
              const studentMap = {};
              studentData.data.forEach(s => {
                studentMap[s.student_id] = s;
              });

              examList.forEach(exam => {
                const student = studentMap[exam.student_id];
                if (student) {
                  exam.in_charge_teacher = student.in_charge_teacher;
                  exam.lecture = student.lecture;
                }
              });
            }
          }
        } catch (studentError) {
          console.error('학생 정보 매칭 실패:', studentError);
        }

        setExamsByPreset(examList);
      }
      setIsExamModalOpen(true);
    } catch (error) {
      console.error('시험 정보 조회 실패:', error);
      setExamsByPreset([]);
      setIsExamModalOpen(true);
    }
  };

  // 시험 정보 모달 닫기
  const handleCloseExamModal = () => {
    setIsExamModalOpen(false);
    setExamsByPreset([]);
    setSelectedPresetName('');
    setSelectedPresetId(null);
  };

  // 배정 성공 후 시험 목록 + 프리셋 목록 갱신
  const handleRefreshAfterAssign = async () => {
    // 시험 정보 모달 데이터 갱신
    if (selectedPresetId) {
      fetchExamsByPreset(selectedPresetId, selectedPresetName);
    }
    // 프리셋 목록 갱신 (배정 숫자 업데이트)
    fetchMyPresets(1, false);
    fetchPublishedPresets(1, false);
    fetchBookmarks();
  };

  // 저장 성공 시 데이터 새로고침 함수
  const handlePresetSaveSuccess = () => {
    // 현재 활성화된 탭에 따라 적절한 데이터 새로고침
    if (activeTab === 'myPresets') {
      fetchMyPresets(myPresetsPage);
    } else if (activeTab === 'publishedPresets') {
      fetchPublishedPresets(publishedPresetsPage);
    } else if (activeTab === 'myBookmarks') {
      fetchBookmarks();
    }
  };

  // 필터 변경 핸들러
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [filterName]: value
      }
    }));
  };

  // 검색 핸들러
  const handleSearch = () => {
    const currentFilters = filters[activeTab];
    const hasSearchCondition = Object.values(currentFilters).some(value => value.trim() !== '');
    
    if (!hasSearchCondition) {
      alert('검색 조건을 입력해주세요.');
      return;
    }
    
    // 검색 모드 활성화
    setIsSearchMode(true);
    
    // 검색 페이지 초기화
    setSearchPages(prev => ({
      ...prev,
      [activeTab]: 1
    }));
    
    // 인피니티 스크롤 상태 초기화
    if (activeTab === 'myPresets') {
      setMyPresetsLoadedPages(1);
    } else if (activeTab === 'publishedPresets') {
      setPublishedPresetsLoadedPages(1);
    }
    
    // 검색 실행
    searchPresets(1, false);
  };

  // 초기화 핸들러
  const handleReset = () => {
    // 필터 초기화
    setFilters(prev => ({
      ...prev,
      [activeTab]: {
        preset_name: '',
        author_name: '',
        description: ''
      }
    }));
    
    // 검색 모드 비활성화
    setIsSearchMode(false);
    
    // 검색 페이지 초기화
    setSearchPages(prev => ({
      ...prev,
      [activeTab]: 1
    }));
    
    // 인피니티 스크롤 상태 초기화
    if (activeTab === 'myPresets') {
      setMyPresetsLoadedPages(1);
    } else if (activeTab === 'publishedPresets') {
      setPublishedPresetsLoadedPages(1);
    }
    
    // 원래 데이터 재로드
    if (activeTab === 'myBookmarks') {
      fetchBookmarks();
    } else if (activeTab === 'myPresets') {
      fetchMyPresets(1, false);
    } else if (activeTab === 'publishedPresets') {
      fetchPublishedPresets(1, false);
    }
  };

  // Enter 키 핸들러
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <>
      <BypassLayout
        loading={isLoading && !myPresets.length && !publishedPresets.length && !myBookmarks.length}
        renderContent={({ isMobileView, getSafeAreaStyle }) => (
        <div 
          className={`flex flex-col ${isMobileView ? 'gap-4' : ''} ${
            isMobileView 
              ? 'h-full overflow-y-auto' 
              : 'h-[calc(100vh-2rem-50px)]'
          }`}
          style={isMobileView ? getSafeAreaStyle() : {}}
        >
          {/* 네비게이션 (데스크톱만) */}
          {!isMobileView && (
            <nav className="shrink-0">
            </nav>
          )}

          {/* 메인 카드 컨테이너 */}
          <div className={`flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg ${
            isMobileView ? 'flex-1' : 'flex-1 max-h-[calc(100vh)]'
          }`}>
            {/* 헤더 섹션 - 탭 버튼들과 생성 버튼 */}
            <div className="border-b bg-white">
              <div className="flex items-center justify-between gap-4">
                {/* 탭 버튼들 */}
                <div className="flex flex-1">
                  <button
                    onClick={() => setActiveTab('myBookmarks')}
                    className={`flex-1 rounded-3xl px-4 py-3 transition-colors focus:outline-none lg:px-6 ${
                      activeTab === 'myBookmarks'
                        ? `bg-primary font-bold text-white ${isMobileView ? 'text-sm' : 'text-base'}`
                        : `font-medium text-gray-700 hover:bg-primary hover:text-white ${isMobileView ? 'text-xs' : 'text-sm'}`
                    }`}
                  >
                    <Bookmark className="mr-1 inline size-3 lg:mr-2 lg:size-4" />
                    북마크
                  </button>
                  <button
                    onClick={() => setActiveTab('myPresets')}
                    className={`flex-1 rounded-3xl px-4 py-3 transition-colors focus:outline-none lg:px-6 ${
                      activeTab === 'myPresets'
                        ? `bg-primary font-bold text-white ${isMobileView ? 'text-sm' : 'text-base'}`
                        : `font-medium text-gray-700 hover:bg-primary hover:text-white ${isMobileView ? 'text-xs' : 'text-sm'}`
                    }`}
                  >
                    <User className="mr-1 inline size-3 lg:mr-2 lg:size-4" />
                    내 커스텀
                  </button>
                  <button
                    onClick={() => setActiveTab('publishedPresets')}
                    className={`flex-1 rounded-3xl px-4 py-3 transition-colors focus:outline-none lg:px-6 ${
                      activeTab === 'publishedPresets'
                        ? `bg-primary font-bold text-white ${isMobileView ? 'text-sm' : 'text-base'}`
                        : `font-medium text-gray-700 hover:bg-primary hover:text-white ${isMobileView ? 'text-xs' : 'text-sm'}`
                    }`}
                  >
                    <Users className="mr-1 inline size-3 lg:mr-2 lg:size-4" />
                    {localStorage.getItem('userType') === 'manager' ? '전체 커스텀' : '공유 커스텀'}
                  </button>
                </div>
              </div>
            </div>
            
            {/* 에러 메시지 */}
            {error && (
              <div className="rounded border border-red-200 bg-red-50 p-4">
                <p className="text-sm text-red-600">오류: {error}</p>
              </div>
            )}
            
            {/* 검색 헤더 영역 추가 */}
            <div className="shrink-0 border-b bg-white">
              <div style={{ 
                padding: isMobileView ? '12px' : '16px'
              }}>
                <div className="mb-4 flex items-center gap-3">
                  <h3 className="text-lg font-semibold">
                    {activeTab === 'myBookmarks' && '북마크'}
                    {activeTab === 'myPresets' && '내 커스텀'}
                    {activeTab === 'publishedPresets' && (localStorage.getItem('userType') === 'manager' ? '전체 커스텀' : '공유 커스텀')}
                  </h3>
                  <p className="text-xs text-gray-500">
                    - {(() => {
                      let totalCount, displayCount;
                      
                      if (activeTab === 'myBookmarks') {
                        totalCount = myBookmarksTotalCount;
                        displayCount = myBookmarks.length;
                      } else if (activeTab === 'myPresets') {
                        totalCount = myPresetsTotalCount;
                        displayCount = myPresets.length;
                      } else {
                        totalCount = publishedPresetsTotalCount;
                        displayCount = publishedPresets.length;
                      }
                      
                      // 총 개수를 알 수 없는 경우 (null) 또는 아직 로드되지 않은 경우 (0이면서 displayCount도 0)
                      if (totalCount === null || (totalCount === 0 && displayCount === 0)) {
                        return (
                          <>
                            <span className="font-semibold text-primary">{displayCount}</span>개 표시 중
                          </>
                        );
                      }
                      
                      // 총 개수를 아는 경우
                      return (
                        <>
                          총 <span className="font-semibold text-primary">{totalCount}</span>개 중{' '}
                          <span className="font-semibold text-primary">{displayCount}</span>개 표시 중
                        </>
                      );
                    })()}
                  </p>
                </div>
                
                {/* 필터 입력 필드들 */}
                <div className="space-y-4">
                  {isMobileView ? (
                    <div>
                      <div className="flex gap-2">
                        <div className="flex-1 grid gap-2 grid-cols-2">
                          <input
                            type="text"
                            placeholder="프리셋 이름"
                            value={filters[activeTab].preset_name}
                            onChange={(e) => handleFilterChange('preset_name', e.target.value)}
                            onKeyDown={handleKeyPress}
                            className="w-full rounded border px-2 py-1 text-xs"
                          />
                          <input
                            type="text"
                            placeholder="작성자"
                            value={filters[activeTab].author_name}
                            onChange={(e) => handleFilterChange('author_name', e.target.value)}
                            onKeyDown={handleKeyPress}
                             className="w-full rounded border px-2 py-1 text-xs"
                             disabled={activeTab === 'myPresets'}
                          />
                          <input
                            type="text"
                            placeholder="설명"
                            value={filters[activeTab].description}
                            onChange={(e) => handleFilterChange('description', e.target.value)}
                            onKeyDown={handleKeyPress}
                            className="w-full rounded border px-2 py-1 text-xs col-span-2"
                          />
                        </div>
                        <div className="flex w-16 flex-col gap-2">
                          <button
                            onClick={handleReset}
                            className="flex-1 min-h-[22px] rounded-3xl bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-300"
                          >
                            초기화
                          </button>
                          <button
                            onClick={handleSearch}
                            className="flex-1 min-h-[22px] rounded-3xl bg-primary px-2 py-0.5 text-xs font-medium text-white hover:bg-primary-hover"
                          >
                            조회
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex gap-2">
                        <div className="flex-1 flex gap-2">
                          <input
                            type="text"
                            placeholder="프리셋 이름"
                            value={filters[activeTab].preset_name}
                            onChange={(e) => handleFilterChange('preset_name', e.target.value)}
                            onKeyDown={handleKeyPress}
                            className="flex-1 rounded border px-2 py-1 text-xs"
                          />
                          <input
                            type="text"
                            placeholder="작성자"
                            value={filters[activeTab].author_name}
                            onChange={(e) => handleFilterChange('author_name', e.target.value)}
                            onKeyDown={handleKeyPress}
                            className="flex-1 rounded border px-2 py-1 text-xs"
                            disabled={activeTab === 'myPresets'}
                          />
                          <input
                            type="text"
                            placeholder="설명"
                            value={filters[activeTab].description}
                            onChange={(e) => handleFilterChange('description', e.target.value)}
                            onKeyDown={handleKeyPress}
                            className="flex-1 rounded border px-2 py-1 text-xs"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleReset}
                            className="px-6 min-h-[22px] rounded-3xl bg-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-300"
                          >
                            초기화
                          </button>
                          <button
                            onClick={handleSearch}
                            className="px-6 min-h-[22px] rounded-3xl bg-primary text-xs font-medium text-white hover:bg-primary-hover"
                          >
                            조회
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* 메인 콘텐츠 */}
            <div className="flex-1 overflow-hidden flex flex-col">

              {/* 탭 콘텐츠 */}
              <div 
                ref={contentScrollRef}
                className={`flex-1 overflow-y-auto bg-gray-50 ${isMobileView ? 'p-3' : 'p-4'}`}
                style={isMobileView ? 
                  { height: 'calc(100vh - 200px)', paddingBottom: '100px' } : 
                  { maxHeight: 'calc(100vh)' }
                }
              >
                {activeTab === 'myBookmarks' && (
                  <div>
                    <FolderView
                      presets={myBookmarks}
                      showBookmark={true}
                      isMyPresetTable={false}
                      activeTab={activeTab}
                      isMobileView={isMobileView}
                      highlightPresetId={highlightPresetId}
                      onToggleBookmark={toggleBookmark}
                      onPreview={previewPreset}
                      onCopy={handleCopyPreset}
                      onEdit={editPreset}
                      onDelete={deletePreset}
                      onTogglePublish={togglePublishStatus}
                      onViewExams={fetchExamsByPreset}
                    />

                    {/* 북마크는 전체 데이터를 한 번에 표시 */}
                    {myBookmarks.length > 0 && (
                      <div className="py-4 text-center text-sm text-gray-500">
                        모든 북마크를 표시 중입니다
                      </div>
                    )}
                  </div>
                )}
                
                {activeTab === 'myPresets' && (
                  <div>
                    <FolderView
                      presets={myPresets}
                      showBookmark={true}
                      isMyPresetTable={true}
                      activeTab={activeTab}
                      isMobileView={isMobileView}
                      highlightPresetId={highlightPresetId}
                      onToggleBookmark={toggleBookmark}
                      onPreview={previewPreset}
                      onCopy={handleCopyPreset}
                      onEdit={editPreset}
                      onDelete={deletePreset}
                      onTogglePublish={togglePublishStatus}
                      onViewExams={fetchExamsByPreset}
                    />

                    {/* 로딩 인디케이터 (인피니티 스크롤) */}
                    {isLoadingMore && (
                      <div className="flex h-16 items-center justify-center">
                        <div className="size-6 animate-spin rounded-full border-b-2 border-gray-900"></div>
                      </div>
                    )}

                    {/* 더 이상 데이터가 없을 때 */}
                    {!hasMoreMyPresets && myPresets.length > 0 && (
                      <div className="py-4 text-center text-sm text-gray-500">
                        모든 프리셋을 불러왔습니다
                      </div>
                    )}
                  </div>
                )}
                
                {activeTab === 'publishedPresets' && (
                  <div>
                    <FolderView
                      presets={publishedPresets}
                      showBookmark={true}
                      isMyPresetTable={false}
                      activeTab={activeTab}
                      isMobileView={isMobileView}
                      highlightPresetId={highlightPresetId}
                      onToggleBookmark={toggleBookmark}
                      onPreview={previewPreset}
                      onCopy={handleCopyPreset}
                      onEdit={editPreset}
                      onDelete={deletePreset}
                      onTogglePublish={togglePublishStatus}
                      onViewExams={fetchExamsByPreset}
                    />

                    {/* 로딩 인디케이터 (인피니티 스크롤) */}
                    {isLoadingMore && (
                      <div className="flex h-16 items-center justify-center">
                        <div className="size-6 animate-spin rounded-full border-b-2 border-gray-900"></div>
                      </div>
                    )}

                    {/* 더 이상 데이터가 없을 때 */}
                    {!hasMorePublishedPresets && publishedPresets.length > 0 && (
                      <div className="py-4 text-center text-sm text-gray-500">
                        모든 공유 프리셋을 불러왔습니다
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* 플로팅 커스텀 생성 버튼 (모바일 & 데스크톱) */}
          <FloatingButton
            variant="plus"
            onClick={handleCreateModalOpen}
            title="커스텀 생성"
            disabled={isCreateModalOpen}
            usePortal={true}
            leftOffsetPx={isMobileView ? 0 : (isExpanded ? 90 : 45)}
          />
        </div>
      )}
    />

    {/* 모든 모달들을 BypassLayout 밖으로 이동 */}
    {/* 교육과정 선택 모달 */}
    <Modal
      isOpen={isCreateModalOpen}
      onClose={() => setIsCreateModalOpen(false)}
      title="교육과정 선택"
      size="md"
      footer={
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setIsCreateModalOpen(false)}
            className="rounded-3xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleCreatePreset}
            disabled={!selectedTreeId || isLoading}
            className={`rounded-3xl px-4 py-2 text-sm font-medium text-white shadow-sm 
              ${!selectedTreeId || isLoading ? 'cursor-not-allowed bg-gray-400' : 'bg-primary hover:bg-primary-hover'}`}
          >
            다음
          </button>
        </div>
      }
    >
      {isLoading ? (
        <div className="my-6 flex justify-center">
          <div className="size-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
        </div>
      ) : error ? (
        <div className="my-4 text-red-500">{error}</div>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          {/* is_closed 상태인 트리만 필터링하여 표시 */}
          {treeList.filter(tree => tree.is_closed).length === 0 ? (
            <p className="text-gray-500">사용 가능한 교육과정 정보가 없습니다.</p>
          ) : (
            <div className="space-y-2">
              {treeList
                .filter(tree => tree.is_closed) // is_closed 상태인 트리만 필터링
                .map(tree => (
                  <div
                    key={tree.tree_id}
                    onClick={() => setSelectedTreeId(tree.tree_id)}
                    className={`cursor-pointer rounded-3xl border p-3 hover:bg-gray-50 
                      ${selectedTreeId === tree.tree_id ? 'border-primary bg-primary-light' : 'border-gray-300'}`}
                  >
                    <div className="font-medium">{tree.tree_name}</div>
                    <div className="text-sm text-gray-500">{tree.edu_ver}</div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </Modal>
    
    {/* 프리셋 생성/편집 모달 */}
    <PresetModal 
      isOpen={isModalOpen} 
      onClose={() => {
        setIsModalOpen(false);
        setSelectedStamp(null);
      }}
      stamp={selectedStamp}
      onSaveSuccess={handlePresetSaveSuccess}
    />
    
    {/* 프리셋 수정 모달 */}
    <PresetModal 
      isOpen={isEditModalOpen} 
      onClose={handleCloseEditModal}
      stamp={selectedStamp}
      onSaveSuccess={handlePresetSaveSuccess}
      isEditMode={true}
      presetToEdit={presetToEdit}
    />
    

    
    {/* 인쇄 모달 */}
    {isPrintModalOpen && printTestData && (
      <PrintPreviewModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        testData={printTestData}
        isMobileView={window.innerWidth < 768}
      />
    )}

    {/* 시험 정보 모달 */}
    <ExamsByPresetModal
      isOpen={isExamModalOpen}
      onClose={handleCloseExamModal}
      presetName={selectedPresetName}
      presetId={selectedPresetId}
      exams={examsByPreset}
      isLoading={isExamLoading}
      onRefreshExams={handleRefreshAfterAssign}
    />
    </>
  );
}

export default PresetPage; 